/**
 * Campaign attribution (BR-31, D-90). A campaign post link (townshop.in/c/<code>) opens the app
 * with `?ref=c&c=<code>`. The code is kept on this device for 7 days — a newer campaign link
 * replaces it (last touch) — and sent with every event, cart order and enquiry, so visits and
 * orders are credited to the post. The server keeps it only for the post's own shop.
 */
import { safeStorage } from '@/lib/storage'

const KEY = 'angadi.campaign'
export const CAMPAIGN_DAYS = 7
const DAY_MS = 24 * 60 * 60 * 1000
const CODE = /^[a-z0-9]{4,8}$/

interface Saved {
  code: string
  at: number
}

/** The campaign code of a link (`?c=`), or null. */
export function campaignCode(search: string): string | null {
  const code = new URLSearchParams(search).get('c')?.trim().toLowerCase() ?? ''
  return CODE.test(code) ? code : null
}

/** Save the code of the link the app was opened with. Returns true when one was saved. */
export function rememberCampaign(search: string = window.location.search, now = Date.now()) {
  const code = campaignCode(search)
  if (!code) return false
  safeStorage.set(KEY, JSON.stringify({ code, at: now } satisfies Saved))
  return true
}

/** The saved code while it is less than 7 days old; null otherwise (and the old one is dropped). */
export function activeCampaign(now = Date.now()): string | null {
  try {
    const saved = JSON.parse(safeStorage.get(KEY) ?? 'null') as Saved | null
    if (
      saved &&
      CODE.test(saved.code) &&
      now - saved.at < CAMPAIGN_DAYS * DAY_MS &&
      now >= saved.at
    ) {
      return saved.code
    }
  } catch {
    /* unreadable: treat as none */
  }
  safeStorage.remove(KEY)
  return null
}

/** `{ campaign }` to spread into a log body, or nothing. */
export function campaignField(now = Date.now()): { campaign?: string } {
  const code = activeCampaign(now)
  return code ? { campaign: code } : {}
}
